<template>
  <div>
    <span id="ennetix-xvisor-web-portal" class="anchor"></span>
    <h2>Ennetix xVisor Web Portal Essentials</h2>
    <p>
      In this section, we present the Ennetix xVisor web portal and how to use the
      portal for operations.
    </p>
    <span id="1-sign-up-and-log-in" class="anchor"></span>
    <h3>1. Sign Up and Log In</h3>
    <p>
      To access Ennetix xVisor, an xVisor-user from an organization (i.e., a client)
      needs to sign up. Sign up is easy and can be done by contacting Ennetix team
      using contact form at
      <strong><a href="https://ennetix.com/contact/">https://ennetix.com/contact/</a></strong>.
      Once the sign up is complete, xVisor-user can log in at
      <strong><a :href="rootLink">{{ rootLink }}</a></strong>.
      Figure 3 shows a screenshot of the login page.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-login.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 3: Ennetix xVisor portal sign up and log in page.</p>
    <span id="2-overview-page" class="anchor"></span>
    <h3>2. Overview Page</h3>
    <p>
      In this section, we explain various components of the Overview page. This is
      the landing page of Ennetix xVisor when an xVisor-user logs in. The goal of
      the page is to make the xVisor-user cognizant of what is going on in its
      network and performances of mission-critical applications. Therefore, the
      Overview page contains panels that summarize the activity and performance of
      various network entities (applications, paths, and network elements).
      Initially, this page is empty. But, as the xVisor-user adds applications and
      network elements, to monitor, various tiles of the Overview page will be
      populated with alert stats, traffic summaries, and performance profiles.
    </p>
    <p>
      There are a few subtle features of the Ennetix xVisor portal, which appear on
      the left and right corners of the top bar (Fig. 4). The left button (right of
      Ennetix xVisor logo when you are hovering over the logo in the corner) expands
      the menu. There's a toggle next to the word "English" that allows you to switch
      from light to dark mode. The language button (right of English) allows the
      xVisor-user to choose different languages.The "+" button (left of xVisor-user
      name) allows the xVisor-user to add new entities (e.g., new XOME or Application)
      on xVisor. Finally, the profile button (right of xVisor-user name) allows the
      xVisor-user to look at their account information or logout.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-xvisor-overview.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 4: Ennetix xVisor Overview page - Part 1.</p>
    <span id="2-1-add-xome" class="anchor"></span>
    <h4>2.1. Add XOME</h4>
    <p>
      Assuming the xVisor-user has downloaded and installed XOME, the first step on
      Ennetix xVisor portal is to register XOME. For that, the xVisor-user can use
      the "+" icon at the top panel of the page to register a XOME (Fig.
      5a).
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-xvisor-overview2.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 5a: Ennetix xVisor Overview page - XOME registration.</p>
    <p>
      xVisor-user can use "Add XOME" button (as shown in Fig. 5b) to
      register XOME with xVisor portal. During XOME installation, xVisor-user can
      follow the instructions provided in a separate "XOME Installation
      Configuration" document (link is provided in "Add XOME" panel).
      Once the task of registering the XOME is complete, the Ennetix xVisor is now
      ready to receive data from that specific XOME. The xVisor-user can add as many
      XOMEs as required to collect network performance data.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-settings-page.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 5b: Ennetix xVisor Settings page - add XOME panel.</p>
    <p>
      After XOME is registered, xVisor-user can configure network elements on the
      network to send packet data by port mirroring or flow data (e.g.,
      NetFlow/SFlow) by configuring routers/switches, to XOME. XOME should also be
      configured to enable data sources to be able to receive various data types
      (e.g., packet captures, NetFlow, SFlow, probe data, etc.). More on this can be
      found on Section 8.2.
    </p>
    <span id="2-2-rest-of-overview-page" class="anchor"></span>
    <h4>2.2. Rest of Overview Page</h4>
    <p>
      The goal of the Overview page is to give the xVisor-user a birds-eye view of
      how the network and monitored applications are performing.
      xVisor presents an overview of application traffic on the network by default (Fig.
      6a). It shows top-five applications over the past 6 hours on the stacked area
      chart in Fig. 6a. All the discovered applications on the network are also
      shown along with their traffic volume in the table beside the chart. Ennetix
      xVisor uses AI-powered methods to classify observed traffic on the network
      into various applications. All discovered applications can be seen along with
      traffic for the past 6 hours in the table (Fig. 6a).
      From this view (Fig. 6a), xVisor-user can use "Add" button to the
      right of application row to add the application to monitor in detail. Once the
      application is added for monitoring, xVisor will keep detailed information on
      each and every application end-user's performance along with issues
      impacting end-user performance at 5-min granularity.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-overview-application-traffic.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 6a: Ennetix xVisor Overview page - Application Traffic</p>
    <p>
      A powerful AI built-in search bar (Fig. 6b1) is on the top of that page. After clicking on the search bar,
      users can input any questions or select from the recommended questions in a dropdown
      menu (Fig. 6b1) to give them a view of how the network and monitored applications perform. When
      users choose from given questions, a new card shows on the page. Users can bookmark the
      card by clicking the bookmark icon or delete the card by clicking the trashbin icon as needed.
      When the user inputs a question, AI will recommend related cards according to the question (Fig. 6b2).
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-overview-dropdown.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 6b1: Ennetix xVisor Overview page - Dropdown Menu</p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-overview-search-bar.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 6b2: Ennetix xVisor Overview page - AI Search Bar</p>
    <span id="2-3-add-custom-application" class="anchor"></span>
    <h4>2.3. Add Custom Application</h4>
    <p>
      On top of discovered applications on the network (as discussed in Section
      2.2), xVisor also gives the xVisor-user options to add custom applications.
      As shown in Fig. 5a, the xVisor-user can use the "+" icon at the top
      panel of the page to add a custom application (Fig. 5a). xVisor-user can add
      two types of custom applications. One type is based on IP prefixes and port
      ranges. Such custom application definition is called
      "Traffic-based". Figure 7a shows the form to add such as
      application. Essentially, using information added in this
      "Traffic-based" application definition, xVisor can classify traffic
      flows to specific user-defined application. In the "Traffic-based"
      application definition, xVisor-user can add various application component
      names, their IP prefixes, port ranges, and protocol (Fig. 7a). In the
      "Traffic-based" application definition, xVisor-user can also select
      to allow xVisor to automatically discover the application's dependent
      network services (required for accessing the application). For auto-discovered
      applications, xVisor automatically discovers these dependent network services.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-add-custom-app.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 7a: Ennetix xVisor Overview page - Add Traffic-based Custom App.</p>
    <p>
      xVisor-user can also manually add custom "Endpoint-based"
      applications to monitor (Fig. 7b). The "Endpoint-based" application
      definition will not be used to classify traffic flows. These definitions will
      only be used to collect performance data by sending probes to the application
      endpoints. An "Endpoint-based" application essentially defines an
      application where only the endpoints (IP or URL) of the application components
      are known. To add an "Endpoint-based" application (Fig. 7b), the
      xVisor-user will need the following information: component name, source
      XOME(s), destination endpoint URL/IP, network layer measurements to collect,
      and application layer measurements to collect. xVisor-user can add multiple
      application components and dependent network services and measurements to be
      collected for each of those components and services.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-add-custom-app2.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 7b: Ennetix xVisor Overview page - Add Endpoint-based Custom App.</p>
    <p>
      After such "Endpoint-based" application definition is added, xVisor
      will start sending custom probes to the defined application and service
      endpoints, and collect various performance data such as path routes, delay,
      loss, jitter, bandwidth, HTTP/S measurements. All the performance information
      for the custom applications will be accessible in Applications Summary/Details
      page. More on these follow.
    </p>
    <span id="3-reports-page" class="anchor"></span>
    <h3>3. Reports Page</h3>
    <p>
      xVisor generates reports on application performance by day in a given month.
      Each day generates various performance metrics for the application servers,
      end-users, and related network elements (Figs. 23a--23b).
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-report-page.png"
        class="responsive-img-lg"
      />
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-report-page2.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig 23a: Ennetix xVisor Reports page - Part 1.</p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-report-page3.png"
        class="responsive-img-lg"
      />
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-report-page4.png"
        class="responsive-img-lg"
      />
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-report-page5.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig 23b: Ennetix xVisor Reports page - Part 2.</p>
    <span id="4-settings-page" class="anchor"></span>
    <h3>4. Settings Page</h3>
    <p>
      On Settings page, various settings of system, xVisor-user, zones, maintenance,
      security, and report configurations can be configured on various tabs (Figs.
      24a—24j).
    </p>
    <span id="4-1-system-tab" class="anchor"></span>
    <h4>4.1. System Tab</h4>
    <p>
      On Systems tab (Fig. 24a), IP whitelist is a list of IPs from where the
      xVisor-user can access the Ennetix xVisor portal. From the edit button at the
      top right, the xVisor-user may input system-specific information such as IP
      whitelist (from where xVisor web portal can be accessed).
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-setting-page-system-configuration.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig 24a: Ennetix xVisor Settings page - System Configuration panel.</p>
    <span id="4-2-xome-s-tab" class="anchor"></span>
    <h4>4.2. XOME(s) Tab</h4>
    <p>
      On XOME(s) tab, xVisor-user can enable various data sources for each of the
      XOMEs. Once the source is active with required parameters, the XOME should be
      able to collect that type of data. For example, to add "Wire Data
      Capture" source (also known as port mirroring/SPAN port data capture),
      xVisor-user needs to specify sniffer interface on XOME where port-mirrored
      data is coming on XOME machine. [Note: If the data sources are not enabled on
      XOME, XOME will not be able to collect data from that specific source.]
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-xome-tab.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig.24b:Ennetix xVisor XOME(s) tab on setting page.</p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-xome-tab2.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig.24c:Ennetix xVisor XOME(s) tab on setting page.</p>
    <span id="4-3-user-s-tab" class="anchor"></span>
    <h4>4.3. User(s) Tab</h4>
    <p>
      This tab is used to manage users of Ennetix xVisor. A list of current
      xVisor-users is given on this page (Fig. 24d).
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-users-tab.png"
        class="responsive-img-lg"
      />
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-users-tab2.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig.24d: Ennetix xVisor User(s) page.</p>
    <p>
      Administrator of a client can add a new user using the "Add User"
      modal (Fig. 24e) and update the xVisor-user using "Update User"
      modal (Fig. 24f). When xVisor-user is added, an email is sent to xVisor-user;
      and the xVisor-user needs to use the link on that email to finish the
      registration process. Until the xVisor-user finishes the registration, the
      "Pending" list (Fig. 24d) will show the pending registrations.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-users-tab3.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 24e: Ennetix xVisor Users page - Add User modal.</p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-users-tab4.png"
        class="responsive-img-lg"
      />
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-users-tab5.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 24f: Ennetix xVisor User(s) tab - Update User modal.</p>
    <span id="4-4-team-s-tab" class="anchor"></span>
    <h4>4.4. Team(s) Tab</h4>
    <p>
      This tab along with xVisor-users tab provides xVisor administrators define
      role-based access to xVisor-users. A team is a group of xVisor-users with
      various access roles defined.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-teams-tab.png"
        class="responsive-img-lg"
      />
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-teams-tab2.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 24g: Ennetix xVisor Team(s) tab on Settings page.</p>
    <p>
      As shown in Figure 24g, teams can be of type "App Admin" where
      xVisor-users will only have access to applications pages, not network elements
      pages. Administrators can define new teams using "Add Team" button
      (Fig. 24g).
    </p>
    <span id="4-5-offices-zones-tab" class="anchor"></span>
    <h4>4.5. Offices/Zones Tab</h4>
    <p>
      This tab lets the administrator to configure zones (such as building a,
      building b, campus z, etc.) and offices (geographical locations) of the client
      (Fig. 24h). In the location hierarchy, zone is contained in an office, i.e.,
      an office can have many zones. While adding zone, administrator can specify IP
      prefixes used in the zone and office name (Fig 24h). While adding office,
      administrator can specify office's geographical location (e.g., city).
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-users-page-office-zone-tab.png"
        class="responsive-img-lg"
      />
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-users-page-office-zone-tab2.png"
        class="responsive-img-lg"
      />
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-users-page-office-zone-tab3.png"
        class="responsive-img-xs"
      />
    </p>
    <p>Fig. 24h: Ennetix xVisor Users page - Offices/Zones tab.</p>
    <span id="4-6-maintenance-tab" class="anchor"></span>
    <h4>4.6. Maintenance Tab</h4>
    <p>
      Maintenance tab (Fig. 24i) lets the administrator to define a maintenance
      window (when application infrastructure maintenance would be done) so that
      alerts/events do not get generated during maintenance. If not configured, many
      false-positive performance events/alerts may be generated due to
      application-infrastructure and/or components not performing well while being
      in maintenance mode.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-users-page-maintenance-tab.png"
        class="responsive-img-lg"
      />
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-users-page-maintenance-tab2.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 24i: Ennetix xVisor Users page - Maintenance tab.</p>
    <span id="4-7-security-tab" class="anchor"></span>
    <h4>4.7. Security Tab</h4>
    <p>
      On Security tab (Fig. 24j), administrators may configure single sign on or
      two-factor authentication. Single sign on allows access control on multiple
      platforms when xVisor-user logs in at one time. Two-factor authentication
      requires two factors to authenticate the xVisor-user, such as a password and a
      randomly-generated code given through email.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-setting-page-security-tab.png"
        class="responsive-img-lg"
      />
    </p>
    <p>
      Fig. 24j: Ennetix xVisor Settings page - Single Sign On and Two-Factor
      Authentication panels.
    </p>
    <span id="4-8-reports-tab" class="anchor"></span>
    <h4>4.8. Reports Tab</h4>
    <p>
      On Reports tab (Fig. 24k), xVisor can be configured to send daily reports in
      few time intervals, e.g., once daily, at 7.00 am (Fig. 24k) (this setting is
      configurable). Reports may include, but are not limited to, application
      statistics, end-user performance statistics, network element statistics, etc.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-setting-page-reports-tab.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 24k: Ennetix xVisor Settings page - Report Configuration panel.</p>
    <span id="4-9-activity-logs" class="anchor"></span>
    <h4>4.9. Activity Logs</h4>
    <p>
      xVisor maintains a user activity log to keep information on xVisor-users'
      activities. This Ennetix xVisor log can also be found on "Activity
      Logs" panel of Settings page (Fig. 24l).
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-service-page-activity-log.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 24l: Ennetix xVisor Services page - Ennetix xVisor Logs.</p>
    <span id="5-user-profile-page" class="anchor"></span>
    <h3>5. User Profile Page</h3>
    <p>
      Each xVisor-user can update their own profile using the "User
      Profile" page (link can be found on the dropdown menu beside the user
      name on the top bar). Here, the xVisor-user can update name and phone
      information, and can also change password.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-users-profile-page.png"
        class="responsive-img-xs"
      />
    </p>
    <p>Fig. 25: Ennetix xVisor User Profile page.</p>
    <span id="5-1-reset-password" class="anchor"></span>
    <h4>5.1. Reset Password</h4>
    <p>
      An xVisor-user can reset their password (in case password is forgotten) for
      the xVisor portal using the login window (Fig. 3). When prompted, an email
      with instructions to reset the password will be sent to the xVisor-user's
      registered email address. xVisor-user can follow the instructions to reset the
      password.
    </p>
    <span id="6-status-page" class="anchor"></span>
    <h3>6. Status Page</h3>
    <p>
      The Status page gives the status of various data collectors (XOMEs) of Ennetix
      xVisor. Figure 26 shows the status of various XOMEs that are deployed in the
      network.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-service-page-xome-status.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 26: Ennetix xVisor Services page - XOME status.</p>
  </div>
</template>

<script>
export default {
  data() {
    return {
      rootLink: window.location.hostname,
    };
  },
};
</script>

<style scoped>
.anchor {
  top: -75px;
  position: relative;
  z-index: 5;
}
</style>
